import { useEffect, useState } from 'react';
import { execApi, SALARY_GLOBAL_KEY } from '../../../services/api';
import { BonusFields, BonusDraft, BonusValues, toDraft, toPayload } from './BonusFields';

// The roster-wide bonus policy. All 19 psychiatrists shipped with identical values,
// so the default belongs in one place; a single doctor can still be given their own
// in the per-therapist editor, which always wins over this.

interface Props { month: string; onClose: () => void; onSaved: () => void; }

export function BonusPolicyEditor({ month, onClose, onSaved }: Props) {
  const [draft, setDraft] = useState<BonusDraft | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [overridden, setOverridden] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [affected, setAffected] = useState<string[] | null>(null);

  useEffect(() => {
    execApi.getBonusPolicy().then((res) => {
      const d = res.data as { policy: BonusValues; overridden: string[] };
      setDraft(toDraft(d.policy));
      setEnabled(d.policy.bonusEnabled);
      setOverridden(d.overridden);
    });
  }, []);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const res = await execApi.saveSalaryRates({
        therapist: SALARY_GLOBAL_KEY, month, rates: toPayload(draft, enabled),
      });
      const d = res.data as { affected?: string[] };
      setAffected(d.affected || []);
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" dir="rtl">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-gray-800">מדיניות בונוס — לכל הרופאים</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl">✕</button>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          ברירת המחדל לכל הרוסטר. רופא עם הגדרה אישית גובר על מה שנקבע כאן.
          {overridden.length > 0 && <span className="text-amber-600"> · שונתה מברירת המחדל המקורית</span>}
        </p>

        {!draft ? (
          <div className="text-sm text-gray-500 py-8 text-center">טוען…</div>
        ) : (
          <>
            <BonusFields draft={draft} enabled={enabled}
              onChange={setDraft} onEnabledChange={setEnabled} />

            {affected && (
              <div className="mt-4 text-xs rounded bg-amber-50 text-amber-800 px-3 py-2">
                {affected.length === 0
                  ? 'נשמר. אף סכום מאושר לא זז.'
                  : `נשמר. ${affected.length} אישורים אופסו כי הסכום שלהם השתנה: ${affected.join(', ')}`}
              </div>
            )}

            <div className="flex items-center gap-2 pt-4 mt-4 border-t">
              <button onClick={save} disabled={saving}
                className="text-sm bg-blue-600 disabled:bg-gray-300 text-white rounded-lg px-6 py-2 font-medium">
                {saving ? 'שומר…' : 'שמור לכולם'}
              </button>
              <button onClick={onClose} className="text-sm text-gray-500 rounded px-4 py-2">סגור</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
